import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '../../../services/api';
import { formatDateTime } from '../../../utils/Utils';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'draft' ? '#64748b15' :
    $status === 'active' ? '#16a34a15' :
    $status === 'closed' ? '#2563eb15' :
    '#9e9e9e15'};
  color: ${({ $status }) =>
    $status === 'draft' ? '#64748b' :
    $status === 'active' ? '#16a34a' :
    $status === 'closed' ? '#2563eb' :
    '#9e9e9e'};
`;

const useSurveyApi = (queryParams) => {
  const { t, i18n } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/surveys?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/surveys/${ids[0]}`);
      } else {
        await api.delete('/v1/surveys', { data: ids });
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const statusLabels = useMemo(() => ({
    draft: t('survey.draft'),
    active: t('survey.active'),
    closed: t('survey.closed'),
  }), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'title', label: t('survey.surveyTitle'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'status', label: t('survey.status'),
      options: {
        filter: true, sort: true,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: [t('survey.draft'), t('survey.active'), t('survey.closed')],
          renderValue: (v) => v,
        },
        filterList: [],
        customFilterListOptions: { render: (v) => v },
        customBodyRender: (v) => <StatusBadge $status={v}>{statusLabels[v] || v}</StatusBadge>,
      },
    },
    {
      name: 'isAnonymous', label: t('survey.isAnonymous'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['true', 'false'],
          renderValue: (v) => v === 'true' ? t('common.yes') : t('common.no'),
        },
        customFilterListOptions: { render: (v) => v === 'true' ? t('common.yes') : t('common.no') },
        customBodyRender: (v) => v ? t('common.yes') : t('common.no'),
      },
    },
    { name: 'questionCount', label: t('survey.questionCount'), options: { filter: false, sort: false } },
    { name: 'participantCount', label: t('survey.participantCount'), options: { filter: false, sort: false } },
    { name: 'completedCount', label: t('survey.completedCount'), options: { filter: false, sort: false } },
    {
      name: 'deadline', label: t('survey.deadline'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => formatDateTime(v, i18n.language),
      },
    },
    {
      name: 'createDate', label: t('survey.createDate'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => formatDateTime(v, i18n.language),
      },
    },
  ], [t, statusLabels]);

  return { columns, data, fetching, pagination, _delete, statusLabels };
};

export { StatusBadge };
export default useSurveyApi;
